/**
 * Product compare
 * -----------------------------------------------------------------------------
 * Shared store + UI for the "add to compare" flow.
 *
 * Public API: window.FreegoCompare
 * Event: document 'compare:change' -> { detail: { items, reason } }
 *
 * This file may be requested by several sections at once, so everything below is
 * guarded to stay idempotent when the browser executes it more than once.
 */
(function () {
  'use strict';

  if (window.FreegoCompare) return;

  var STORAGE_KEY = 'freego:compare:v1';
  var DISMISS_KEY = 'freego:compare:dismissed';
  var CHANGE_EVENT = 'compare:change';
  var DEFAULT_COMPARE_URL = '/pages/product-compare';

  function getStorage(type) {
    try {
      var storage = window[type];
      var probe = '__freego_probe__';
      storage.setItem(probe, '1');
      storage.removeItem(probe);
      return storage;
    } catch (error) {
      return null;
    }
  }

  var local = getStorage('localStorage');
  var session = getStorage('sessionStorage');
  var fallback = { items: null, dismissed: false };

  function normalize(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var id = raw.id === 0 ? '0' : String(raw.id || '').trim();
    if (!id) return null;

    return {
      id: id,
      handle: String(raw.handle || ''),
      title: String(raw.title || ''),
      image: String(raw.image || ''),
      url: String(raw.url || (raw.handle ? '/products/' + raw.handle : ''))
    };
  }

  var store = {
    _items: null,

    _read: function () {
      if (this._items) return this._items;

      var parsed = [];
      if (local) {
        try {
          parsed = JSON.parse(local.getItem(STORAGE_KEY)) || [];
        } catch (error) {
          parsed = [];
        }
      } else if (fallback.items) {
        parsed = fallback.items;
      }

      this._items = (Array.isArray(parsed) ? parsed : []).map(normalize).filter(Boolean);
      return this._items;
    },

    _write: function (items, reason) {
      this._items = items;

      if (local) {
        try {
          local.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (error) {
          fallback.items = items;
        }
      } else {
        fallback.items = items;
      }

      document.dispatchEvent(
        new CustomEvent(CHANGE_EVENT, {
          detail: { items: items.slice(), reason: reason || 'update' }
        })
      );
    },

    getItems: function () {
      return this._read().slice();
    },

    getIds: function () {
      return this._read().map(function (item) {
        return item.id;
      });
    },

    has: function (id) {
      var key = String(id);
      return this._read().some(function (item) {
        return item.id === key;
      });
    },

    count: function () {
      return this._read().length;
    },

    add: function (raw) {
      var item = normalize(raw);
      if (!item || this.has(item.id)) return false;

      this.setDismissed(false, true);
      this._write(this._read().concat([item]), 'add');
      return true;
    },

    remove: function (id) {
      var key = String(id);
      var next = this._read().filter(function (item) {
        return item.id !== key;
      });
      if (next.length === this._read().length) return false;

      this._write(next, 'remove');
      return true;
    },

    toggle: function (raw) {
      var item = normalize(raw);
      if (!item) return false;

      if (this.has(item.id)) {
        this.remove(item.id);
        return false;
      }

      this.add(item);
      return true;
    },

    clear: function () {
      this._write([], 'clear');
    },

    /* Replaces the whole selection, keeping the given order. */
    setAll: function (rawItems) {
      var seen = {};
      var items = (Array.isArray(rawItems) ? rawItems : [])
        .map(normalize)
        .filter(function (item) {
          if (!item || seen[item.id]) return false;
          seen[item.id] = true;
          return true;
        });

      this._write(items, 'set');
    },

    isDismissed: function () {
      if (session) return session.getItem(DISMISS_KEY) === '1';
      return fallback.dismissed;
    },

    setDismissed: function (value, silent) {
      if (this.isDismissed() === !!value) return;

      if (session) {
        if (value) session.setItem(DISMISS_KEY, '1');
        else session.removeItem(DISMISS_KEY);
      } else {
        fallback.dismissed = !!value;
      }

      if (!silent) {
        document.dispatchEvent(
          new CustomEvent(CHANGE_EVENT, {
            detail: { items: this.getItems(), reason: value ? 'dismiss' : 'restore' }
          })
        );
      }
    },

    subscribe: function (callback) {
      document.addEventListener(CHANGE_EVENT, callback);
    },

    unsubscribe: function (callback) {
      document.removeEventListener(CHANGE_EVENT, callback);
    }
  };

  /* Keep tabs in sync. */
  window.addEventListener('storage', function (event) {
    if (event.key !== STORAGE_KEY) return;
    store._items = null;
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: { items: store.getItems(), reason: 'external' }
      })
    );
  });

  function isComparePage() {
    return !!document.querySelector('[data-compare-page]');
  }

  /**
   * <compare-toggle> - the "Add to compare" control inside a product card.
   * Re-syncs on connect, which covers both `shopify:section:load` and Dawn's
   * facet filtering (both re-insert the grid markup).
   */
  if (!window.customElements.get('compare-toggle')) {
    var CompareToggle = class extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="checkbox"]');
        if (!this.input) return;

        this.textEl = this.querySelector('[data-compare-text]');
        this.handleInput = this.handleInput.bind(this);
        this.handleStoreChange = this.handleStoreChange.bind(this);

        this.input.addEventListener('change', this.handleInput);
        store.subscribe(this.handleStoreChange);
        this.sync();
      }

      disconnectedCallback() {
        if (this.input) this.input.removeEventListener('change', this.handleInput);
        store.unsubscribe(this.handleStoreChange);
      }

      get item() {
        return {
          id: this.dataset.compareId,
          handle: this.dataset.compareHandle,
          title: this.dataset.compareTitle,
          image: this.dataset.compareImage,
          url: this.dataset.compareUrl
        };
      }

      handleInput() {
        store.toggle(this.item);
      }

      handleStoreChange() {
        this.sync();
      }

      sync() {
        var active = store.has(this.dataset.compareId);

        this.input.checked = active;
        this.classList.toggle('is-active', active);

        if (this.textEl) {
          var label = active ? this.dataset.addedLabel : this.dataset.addLabel;
          if (label) this.textEl.textContent = label;
        }
      }
    };

    window.customElements.define('compare-toggle', CompareToggle);
  }

  /**
   * <compare-tray> - sticky bar listing everything the visitor picked.
   */
  if (!window.customElements.get('compare-tray')) {
    window.customElements.define(
      'compare-tray',
      class extends HTMLElement {
        connectedCallback() {
          this.panel = this.querySelector('[data-compare-panel]');
          this.list = this.querySelector('[data-compare-list]');
          this.scroller = this.querySelector('[data-compare-scroller]');
          this.template = this.querySelector('template');
          this.cta = this.querySelector('[data-compare-cta]');
          this.hint = this.querySelector('[data-compare-hint]');
          this.reopen = this.querySelector('[data-compare-reopen]');
          this.countEls = this.querySelectorAll('[data-compare-count]');

          if (!this.panel || !this.list || !this.template || !this.cta) return;

          this.minItems = parseInt(this.dataset.minItems || '2', 10);
          this.compareUrl = this.dataset.compareUrl || DEFAULT_COMPARE_URL;
          this.renderedIds = [];

          this.handleStoreChange = this.handleStoreChange.bind(this);
          this.handleClick = this.handleClick.bind(this);
          this.handleCtaClick = this.handleCtaClick.bind(this);
          this.updateMetrics = this.updateMetrics.bind(this);

          store.subscribe(this.handleStoreChange);
          this.addEventListener('click', this.handleClick);
          this.cta.addEventListener('click', this.handleCtaClick);
          window.addEventListener('resize', this.updateMetrics);
          if (this.scroller) this.scroller.addEventListener('scroll', this.updateMetrics);

          this.render(true);
        }

        disconnectedCallback() {
          store.unsubscribe(this.handleStoreChange);
          this.removeEventListener('click', this.handleClick);
          if (this.cta) this.cta.removeEventListener('click', this.handleCtaClick);
          window.removeEventListener('resize', this.updateMetrics);
          if (this.scroller) this.scroller.removeEventListener('scroll', this.updateMetrics);

          document.body.classList.remove('compare-tray-open');
          document.documentElement.style.setProperty('--compare-tray-height', '0px');
        }

        handleStoreChange() {
          this.render();
        }

        handleClick(event) {
          var removeButton = event.target.closest('[data-compare-remove]');
          if (removeButton) {
            this.removeItem(removeButton);
            return;
          }

          if (event.target.closest('[data-compare-clear]')) {
            store.clear();
            return;
          }

          if (event.target.closest('[data-compare-close]')) {
            store.setDismissed(true);
            return;
          }

          if (event.target.closest('[data-compare-reopen]')) {
            store.setDismissed(false);
          }
        }

        handleCtaClick(event) {
          if (this.cta.getAttribute('aria-disabled') !== 'true') return;

          event.preventDefault();
          if (this.hint) {
            this.hint.classList.remove('is-flashing');
            /* Restart the attention animation. */
            void this.hint.offsetWidth;
            this.hint.classList.add('is-flashing');
          }
        }

        removeItem(button) {
          var node = button.closest('[data-compare-item]');
          var id = button.dataset.compareRemove;

          if (!node || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            store.remove(id);
            return;
          }

          node.classList.add('is-leaving');
          window.setTimeout(function () {
            store.remove(id);
          }, 180);
        }

        buildItem(item) {
          var node = this.template.content.firstElementChild.cloneNode(true);
          var link = node.querySelector('[data-compare-item-link]');
          var image = node.querySelector('[data-compare-item-image]');
          var title = node.querySelector('[data-compare-item-title]');
          var remove = node.querySelector('[data-compare-remove]');

          node.setAttribute('data-compare-item', item.id);

          if (link) link.href = item.url || '#';
          if (title) title.textContent = item.title;

          if (image) {
            if (item.image) {
              image.src = item.image;
              image.alt = item.title;
            } else {
              image.remove();
            }
          }

          if (remove) {
            remove.setAttribute('data-compare-remove', item.id);
            var label = remove.getAttribute('aria-label') || '';
            if (label.indexOf('%s') !== -1) {
              remove.setAttribute('aria-label', label.replace('%s', item.title));
            }
          }

          return node;
        }

        buildUrl(items) {
          var url;
          try {
            url = new URL(this.compareUrl, window.location.origin);
          } catch (error) {
            url = new URL(DEFAULT_COMPARE_URL, window.location.origin);
          }

          url.searchParams.set(
            'ids',
            items
              .map(function (item) {
                return item.id;
              })
              .join(',')
          );

          return url.pathname + url.search;
        }

        render(initial) {
          var items = store.getItems();
          var ids = items.map(function (item) {
            return item.id;
          });
          var previous = this.renderedIds;

          this.countEls.forEach(function (el) {
            el.textContent = items.length;
          });

          this.list.innerHTML = '';
          items.forEach(
            function (item) {
              var node = this.buildItem(item);
              if (!initial && previous.indexOf(item.id) === -1) node.classList.add('is-entering');
              this.list.appendChild(node);
            }.bind(this)
          );

          this.renderedIds = ids;

          var ready = items.length >= this.minItems;
          this.cta.href = this.buildUrl(items);
          this.cta.classList.toggle('is-disabled', !ready);
          this.cta.setAttribute('aria-disabled', String(!ready));
          if (this.hint) {
            this.hint.hidden = ready || items.length === 0;
            if (ready) this.hint.classList.remove('is-flashing');
          }

          var dismissed = store.isDismissed();
          var onComparePage = isComparePage();

          this.setVisible(items.length > 0 && !dismissed && !onComparePage);
          if (this.reopen) this.reopen.hidden = !(items.length > 0 && dismissed && !onComparePage);

          window.requestAnimationFrame(
            function () {
              this.updateMetrics();

              /* Second frame: the entering state has been painted, so removing
                 the class now actually animates. */
              window.requestAnimationFrame(
                function () {
                  this.list.querySelectorAll('.is-entering').forEach(function (node) {
                    node.classList.remove('is-entering');
                  });
                }.bind(this)
              );
            }.bind(this)
          );
        }

        setVisible(visible) {
          this.classList.toggle('is-visible', visible);
          this.setAttribute('aria-hidden', String(!visible));
          document.body.classList.toggle('compare-tray-open', visible);

          if (!visible) {
            document.documentElement.style.setProperty('--compare-tray-height', '0px');
          }
        }

        updateMetrics() {
          if (this.classList.contains('is-visible')) {
            document.documentElement.style.setProperty(
              '--compare-tray-height',
              this.panel.offsetHeight + 'px'
            );
          }

          if (!this.scroller) return;

          var overflowing = this.scroller.scrollWidth - this.scroller.clientWidth > 2;
          var atEnd =
            this.scroller.scrollLeft + this.scroller.clientWidth >= this.scroller.scrollWidth - 2;

          this.scroller.classList.toggle('is-overflowing', overflowing);
          this.scroller.classList.toggle('is-scroll-start', this.scroller.scrollLeft > 2);
          this.scroller.classList.toggle('is-scroll-end', overflowing && atEnd);
        }
      }
    );
  }

  window.FreegoCompare = {
    EVENT: CHANGE_EVENT,
    getItems: store.getItems.bind(store),
    getIds: store.getIds.bind(store),
    has: store.has.bind(store),
    count: store.count.bind(store),
    add: store.add.bind(store),
    remove: store.remove.bind(store),
    toggle: store.toggle.bind(store),
    clear: store.clear.bind(store),
    setAll: store.setAll.bind(store),
    subscribe: store.subscribe.bind(store),
    unsubscribe: store.unsubscribe.bind(store)
  };
})();
